import React, {Component} from "react"
import tinycolor from "tinycolor2"
import validator from "validator"

import {SketchPicker} from "react-color"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import id from "@irati/utils/id"
import {ValidationButton} from "@irati/ui/src/FormItem"
import Form from "@irati/ui/src/Form"
import {H1, Body} from "@irati/ui/src/Typography"
import theme from "@irati/ui/src/global/theme"
import Validation from "@irati/utils/validation"
import Button from "@irati/ui/src/Button"
import adminTracking from "@irati-tracking/admin"
import Flex from "@irati/ui/src/Flex"
import Input from "@irati/ui/src/FormItem/Input"
import ColorPreview from "./styled/ColorPreview"
import CustomColorPreview from "./styled/CustomColorPreview"

import BackLink from "./BackLink"

interface Props extends InjectIntl {
  onColorSelected: () => void
  onSkip: () => void
  colorsList: [[number]] // [[r,g,b], ...]
  showSkip: boolean
  handleBackToSelect: () => void
}
interface State {
  selectedColor: string | null
  selectedId: string | null
  customInput: boolean
  customColorInput: string
}

class PickColorStep extends Component<Props, State> {
  state = {
    selectedColor: null,
    selectedId: null,
    customInput: false,
    customColorInput: ""
  }

  updateCustomColor = (value: string) => {
    this.setState({customColorInput: value || ""})
  }

  getCustomColorSelectedHandler = () => {
    this.setState((state) => ({
      selectedColor: null,
      selectedId: null,
      customInput: !state.customInput
    }))
  }

  getColorSelectedHandler = (color: string, colorItemId: string) => {
    this.setState((state) => ({
      customInput: false,
      selectedId: colorItemId === state.selectedId ? null : colorItemId,
      selectedColor: colorItemId === state.selectedId ? null : color
    }))
  }

  onSelectedColorSubmit = () => {
    const {selectedColor, customInput, customColorInput} = this.state
    const {onColorSelected} = this.props
    if (customInput) {
      let userColor = customColorInput
      if (!(userColor[0] === "#")) {
        userColor = `#${userColor}`
      }
      if (validator.isHexColor(userColor)) {
        onColorSelected(userColor)
      }
    } else {
      onColorSelected(selectedColor)
    }
    adminTracking.updateColorScheme()
  }

  handleCustomPicker = (color: {hex: string}) => {
    this.setState({selectedColor: color.hex})
    this.updateCustomColor(color.hex)
  }

  render() {
    const idNamespace = id("pick-color-step", this.props)
    const {selectedColor, selectedId, customInput, customColorInput} = this.state
    const {formatMessage, colorsList, showSkip, onSkip, handleBackToSelect} = this.props
    return (
      <div id={idNamespace} className="pick-colors">
        <BackLink onClick={handleBackToSelect} text="admin.components.brandSettings.logoAndColours" />
        <H1 marginBottom={4} id={id("title", idNamespace)}>
          <FormattedMessage id="admin.components.pickColorStep.title" />
        </H1>
        <Body id={id("sub-title", idNamespace)}>
          <FormattedMessage id="admin.components.pickColorStep.subTitle" />
        </Body>
        <Flex marginTop={4} wrap="wrap">
          {colorsList &&
            colorsList.map((c, i) => {
              const bgCol = `rgb(${c[0]}, ${c[1]}, ${c[2]})`
              const colorString = tinycolor(bgCol).toHexString()
              const isLight = tinycolor(bgCol).isLight()
              const itemId = `col-${i}`
              return (
                <ColorPreview
                  id={id(`btn-color--${i}`, idNamespace)}
                  key={itemId}
                  onClick={() => this.getColorSelectedHandler(colorString, itemId)}
                  isLight={isLight}
                  backgroundColor={bgCol}
                  color={colorString}
                  selectedId={selectedId}
                  itemId={itemId}
                  hexCode={colorString}
                />
              )
            })}
          <CustomColorPreview
            id={id("btn-color--custom", idNamespace)}
            isValidHex={validator.isHexColor(customColorInput)}
            selected={customInput}
            isAnySelected={selectedId}
            color={customColorInput[0] === "#" ? customColorInput : `#${customColorInput}`}
            key="c-x"
            onClick={this.getCustomColorSelectedHandler}>
            {customInput && validator.isHexColor(customColorInput) ? (
              "\u00a0"
            ) : (
              <FormattedMessage id="admin.components.pickColorStep.customColorPreviewCircle.custom" />
            )}
          </CustomColorPreview>
        </Flex>
        <Form id={id("form", idNamespace)}>
          {customInput && (
            <Flex margin="0 auto" alignContent="center" direction="column" alignItems="center" justifyContent="center" maxWidth="420px">
              <Input
                id={id("field-hex", idNamespace)}
                name="customColor"
                label=""
                placeholder={theme.colors.perkbox.blue}
                onChange={this.updateCustomColor}
                value={customColorInput}
                validation={Validation.string(formatMessage({id: "admin.components.pickColorStep.input.validation.required"})).test(
                  "is-valid-hex-colour",
                  formatMessage({id: "admin.components.pickColorStep.input.validation.hexColour"}),
                  () => validator.isHexColor(customColorInput)
                )}
              />
              <SketchPicker color={selectedColor || "#FFFFFF"} onChangeComplete={this.handleCustomPicker} />
            </Flex>
          )}
          <br />
          {selectedColor || customInput ? (
            <ValidationButton id={id("btn-submit", idNamespace)} primary onClick={this.onSelectedColorSubmit}>
              <FormattedMessage id="admin.components.pickColorStep.validationButton.setColour" />
            </ValidationButton>
          ) : (
            showSkip && (
              <Button id={id("btn-skip", idNamespace)} onClick={onSkip}>
                <FormattedMessage id="admin.components.pickColorStep.button.skip" />
              </Button>
            )
          )}
        </Form>
      </div>
    )
  }
}

export default injectIntl(PickColorStep)
